import * as React from 'react';
import './order-detail.scss';
import { Modal } from 'components/modal/modal';
import { ToIranCurrency } from 'core/utils/decimalExtentions';
import { Badge } from 'components/badge/badge';
import moment from 'jalali-moment';
import { Spin } from 'antd';
import { getOrderDetailByTrackingCode } from 'core/repositories/orders-transactions';
import { CopyOutlined } from '@ant-design/icons';
import { copyToClipboard } from '../../../../../core/utils/copyToClipboard';
import Countdown from 'antd/lib/statistic/Countdown';
import { OrderTransaction, OrderTransactionDetail } from '../../../../../core/domain';

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    trackingId: string;
    selectedRow: OrderTransaction;
}

export const OrderDetailModal: React.FC<Props> = ({ isOpen, setIsOpen, trackingId, selectedRow }) => {
    const [loading, setLoading] = React.useState(false);
    const [order, setOrder] = React.useState<OrderTransactionDetail>();
    const [timer, setTimer] = React.useState<any>(0);

    const fetchOrderDetailByTrackingCode = async () => {
        if (isOpen) {
            await getOrderDetailByTrackingCode(setLoading, setOrder, setTimer, trackingId);
        }
    };

    React.useEffect(() => {
        fetchOrderDetailByTrackingCode();
    }, [isOpen]);

    return (
        <Modal className="pmx-order-track-detail" isOpen={isOpen} setIsOpen={setIsOpen}>
            {loading ? (
                <Spin spinning={loading} />
            ) : (
                <>
                    <h3>جزئیات سفارش</h3>
                    {order && (
                        <>
                            <div className="row">
                                <span>کد پیگیری</span>
                                <p className="en">
                                    <CopyOutlined
                                        onClick={() => copyToClipboard(order.orderTransactionTrackingCode, 'کد پیگیری')}
                                    />
                                    {order.orderTransactionTrackingCode}
                                </p>
                            </div>
                            <div className="row">
                                <span>ارز مبدا</span>
                                <p className="en">{order.sourceSymbol}</p>
                            </div>
                            <div className="row">
                                <span>ارز مقصد</span>
                                <p className="en">{order.destinationSymbol}</p>
                            </div>
                            <div className="row">
                                <span>مقدار واریزی</span>
                                <p>
                                    {order.sourceSymbol === 'IRT'
                                        ? ToIranCurrency(order.sourceAmount)
                                        : order.sourceAmount}
                                </p>
                            </div>
                            <div className="row">
                                <span>مقدار دریافتی</span>
                                <p>
                                    {order.destinationSymbol === 'IRT'
                                        ? ToIranCurrency(order.destinationAmount)
                                        : order.destinationAmount}
                                </p>
                            </div>
                            {order.deposit?.voucher && (
                                <div className="row">
                                    <span>کد ووچر</span>
                                    <p>{order.deposit?.voucher?.voucherCode}</p>
                                </div>
                            )}
                            {order.deposit?.voucher && (
                                <div className="row">
                                    <span>کد فعالساز ووچر</span>
                                    <p>{order.deposit?.voucher?.voucherActivationCode}</p>
                                </div>
                            )}
                            {order.deposit?.voucher && (
                                <div className="row">
                                    <span>بچ نامبر</span>
                                    <p className="en long-text">
                                        <CopyOutlined
                                            onClick={() =>
                                                copyToClipboard(order.deposit?.voucher?.batchNumber, 'بچ نامبر')
                                            }
                                        />
                                        {order.deposit?.voucher?.batchNumber}
                                    </p>
                                </div>
                            )}
                            {order.deposit?.txId && (
                                <div className="row copy">
                                    <span>tx-id</span>
                                    <p className="en long-text">{order.deposit?.txId}</p>
                                    <CopyOutlined onClick={() => copyToClipboard(order.deposit?.txId as string, 'tx-id')} />
                                </div>
                            )}
                            {order.deposit?.digitalReceipt && (
                                <div className="row copy">
                                    <span>رسید دیجیتالی</span>
                                    <p className={'long-text'}>{order.deposit?.digitalReceipt}</p>
                                    <CopyOutlined
                                        onClick={() => copyToClipboard(order.deposit?.digitalReceipt as string, 'رسید دیجیتالی')}
                                    />
                                </div>
                            )}
                            {order.deposit?.bankAccount?.iBan && (
                                <div className="row">
                                    <span>شماره شبا</span>
                                    <p>{order.deposit?.bankAccount?.iBan}</p>
                                </div>
                            )}
                            {order.deposit?.wallet?.walletAddress && (
                                <div className="row">
                                    <span>آدرس کیف پول</span>
                                    <p className="en">{order.deposit?.wallet?.walletAddress}</p>
                                </div>
                            )}
                            <div className="row">
                                <span>نوع سفارش</span>
                                <p>
                                    {order.sourceSymbol === 'IRT'
                                        ? 'خرید'
                                        : order.destinationSymbol === 'IRT'
                                            ? 'فروش'
                                            : 'تبدیل'}
                                </p>
                            </div>
                            <div className="row">
                                <span>اکانت ارز مقصد</span>
                                <p>{order.destinationSymbol}</p>
                            </div>
                            <div className="row">
                                <span>تاریخ و زمان سفارش</span>
                                <p>
                                    {moment.utc(order.createdOn).local().locale('fa').format('YYYY/MM/DD')} |{' '}
                                    {moment.utc(order.createdOn).local().locale('fa').format('HH:mm')}
                                </p>
                            </div>
                            {(order.orderTransactionState?.id === 9 || order.orderTransactionState?.id === 10) && (
                                <div className="row">
                                    <span>زمان باقی مانده تا واریز</span>
                                    <Countdown value={timer} />
                                </div>
                            )}
                            <div className="row">
                                <span>وضعیت</span>
                                <p>
                                    <Badge
                                        text={order.orderTransactionState?.title ?? ''}
                                        type={
                                            (order.orderTransactionState?.title ?? '').includes('کنسل شده') ||
                                                (order.orderTransactionState?.title ?? '').includes('ناموفق')
                                                ? 'error'
                                                : (order.orderTransactionState?.title ?? '').includes('در حال') ||
                                                    (order.orderTransactionState?.title ?? '').includes('انتظار')
                                                    ? 'waiting'
                                                    : (order.orderTransactionState?.title ?? '').includes('نشده')
                                                        ? 'error'
                                                        : 'success'
                                        }
                                    />
                                </p>
                            </div>
                            {order.deposit?.receptMedia && (
                                <div className="row">
                                    <span>رسید</span>
                                    <p>{order.deposit?.receptMedia?.fileName}</p>
                                </div>
                            )}
                            {order.deposit?.network && (
                                <div className="row">
                                    <span>شبکه</span>
                                    <p>{order.deposit?.network?.name}</p>
                                </div>
                            )}
                            {order.deposit?.network && (
                                <div className="row">
                                    <span>هزینه شبکه</span>
                                    <p>{order.deposit?.network?.fee != null ? ToIranCurrency(order.deposit?.network?.fee) : ''}</p>
                                </div>
                            )}
                            {order.deposit?.transactionState && (
                                <div className="row">
                                    <span>وضعیت تراکنش</span>
                                    <p>{order.deposit?.transactionState?.title}</p>
                                </div>
                            )}
                            {order.deposit?.transactionType && (
                                <div className="row">
                                    <span>نوع تراکنش</span>
                                    <p>{order.deposit?.transactionType?.title}</p>
                                </div>
                            )}
                            {order.withdrawal && (
                                <>
                                    <div className="row">
                                        <span>مقدار برداشت</span>
                                        <p>
                                            {order.withdrawal?.symbol === 'IRT'
                                                ? ToIranCurrency(order.withdrawal?.amount as number)
                                                : order.withdrawal?.amount}
                                        </p>
                                    </div>
                                    {order.withdrawal?.voucher && (
                                        <div className="row">
                                            <span>کد ووچر برداشت</span>
                                            <p>{order.withdrawal?.voucher?.voucherCode}</p>
                                        </div>
                                    )}
                                    {order.withdrawal?.voucher && (
                                        <div className="row">
                                            <span>کد فعالساز ووچر برداشت</span>
                                            <p>{order.withdrawal?.voucher?.voucherActivationCode}</p>
                                        </div>
                                    )}
                                    {order.withdrawal?.voucher && (
                                        <div className="row">
                                            <span>بچ نامبر برداشت</span>
                                            <p className="en long-text">
                                                <CopyOutlined
                                                    onClick={() =>
                                                        copyToClipboard(
                                                            order.withdrawal?.voucher?.batchNumber,
                                                            'بچ نامبر برداشت',
                                                        )
                                                    }
                                                />
                                                {order.withdrawal?.voucher?.batchNumber}
                                            </p>
                                        </div>
                                    )}
                                    {order.withdrawal?.txId && (
                                        <div className="row copy">
                                            <span>tx-id برداشت</span>
                                            <p className="en long-text">{order.withdrawal?.txId}</p>
                                            <CopyOutlined
                                                onClick={() => copyToClipboard(order.withdrawal?.txId as string, 'tx-id برداشت')}
                                            />
                                        </div>
                                    )}
                                    {order.withdrawal?.digitalReceipt && (
                                        <div className="row copy">
                                            <span>رسید دیجیتالی برداشت</span>
                                            <p className={'long-text'}>{order.withdrawal?.digitalReceipt}</p>
                                            <CopyOutlined
                                                onClick={() =>
                                                    copyToClipboard(
                                                        order.withdrawal?.digitalReceipt as string,
                                                        'رسید دیجیتالی برداشت',
                                                    )
                                                }
                                            />
                                        </div>
                                    )}
                                    {order.withdrawal?.bankAccount?.iBan && (
                                        <div className="row">
                                            <span>شماره شبا برداشت</span>
                                            <p>{order.withdrawal?.bankAccount?.iBan}</p>
                                        </div>
                                    )}
                                    {order.withdrawal?.wallet?.walletAddress && (
                                        <div className="row">
                                            <span>آدرس کیف پول برداشت</span>
                                            <p className="en">{order.withdrawal?.wallet?.walletAddress}</p>
                                        </div>
                                    )}
                                    {order.withdrawal?.receptMedia && (
                                        <div className="row">
                                            <span>رسید برداشت</span>
                                            <p>{order.withdrawal?.receptMedia?.fileName}</p>
                                        </div>
                                    )}
                                    {order.withdrawal?.network && (
                                        <div className="row">
                                            <span>شبکه برداشت</span>
                                            <p>{order.withdrawal?.network?.name}</p>
                                        </div>
                                    )}
                                    {order.withdrawal?.network && (
                                        <div className="row">
                                            <span>هزینه شبکه برداشت</span>
                                            <p>{order.withdrawal?.network?.fee != null ? ToIranCurrency(order.withdrawal?.network?.fee) : ''}</p>
                                        </div>
                                    )}
                                    {order.withdrawal?.transactionState && (
                                        <div className="row">
                                            <span>وضعیت تراکنش برداشت</span>
                                            <p>{order.withdrawal?.transactionState?.title}</p>
                                        </div>
                                    )}
                                    {order.withdrawal?.transactionType && (
                                        <div className="row">
                                            <span>نوع تراکنش برداشت</span>
                                            <p>{order.withdrawal?.transactionType?.title}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </Modal>
    );
};
